import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "@/lib/hooks/useAuth";
import { signOut } from "@/lib/auth";
import { MarketingIcon } from "./icons";

export interface MarketingAppBarItem {
  label: string;
  href: string;
}

interface Props {
  items: MarketingAppBarItem[];
  activeItem?: string;
  brandHref?: string;
  brandOnClick?: (
    event: ReactMouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
  mobileMenuOpen: boolean;
  navScrolled: boolean;
  onToggleMenu: () => void;
  onItemClick?: (
    href: string,
  ) => (event: ReactMouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}

function isAnchorLink(href: string) {
  return href.startsWith("#");
}

function NavItem({
  href,
  className,
  children,
  onClick,
}: {
  href: string;
  className: string;
  children: ReactNode;
  onClick?: (event: ReactMouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}) {
  if (isAnchorLink(href)) {
    return (
      <a href={href} onClick={onClick} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} onClick={onClick} className={className}>
      {children}
    </Link>
  );
}

export function MarketingAppBar({
  items,
  activeItem,
  brandHref = "/",
  brandOnClick,
  mobileMenuOpen,
  navScrolled,
  onToggleMenu,
  onItemClick,
}: Props) {
  const auth = useAuth();
  const nav = useNavigate();
  const showLogin = !auth.loading && !auth.user;
  const showLogout = !auth.loading && !!auth.user;
  const handleLogout = async () => {
    await signOut();
    nav("/");
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 px-3 pt-7 sm:px-5 sm:pt-8 lg:pt-9">
      <div className="pointer-events-auto mx-auto w-full max-w-[820px] transition-all duration-300">
        <header
          className="rounded-[20px] border border-[#EEF4F8] bg-white px-2 py-1 text-[var(--cs-navy)] transition-all duration-300 sm:rounded-[22px] sm:px-2.5 sm:py-1.5"
          style={{
            boxShadow: navScrolled
              ? "0 20px 52px rgb(0 0 0 / 0.14)"
              : "0 16px 44px rgb(0 0 0 / 0.10)",
          }}
        >
          <div className="flex items-center gap-2 transition-all duration-300 sm:gap-2.5">
            <NavItem
              href={brandHref}
              onClick={brandOnClick}
              className="flex min-w-0 items-center gap-2 rounded-full py-0.5 pl-3 pr-2 sm:pl-4 sm:pr-2.5"
            >
              <span className="sr-only">{brandHref === "/" ? "Ga naar home" : "Ga naar boven"}</span>
              <img
                src="/carmen-logo.png"
                alt="Carmen"
                className="-my-2 h-9 w-auto sm:h-11"
                draggable={false}
              />
            </NavItem>

            <nav className="hidden flex-1 items-center justify-center gap-1 sm:flex sm:gap-1.5" aria-label="Primary">
              {items.map((item) => {
                const active = activeItem === item.href;

                return (
                  <NavItem
                    key={item.label}
                    href={item.href}
                    onClick={onItemClick?.(item.href)}
                    className={`rounded-full px-2.5 py-1 text-[13px] font-medium transition-all duration-200 sm:px-3 sm:text-[13px] lg:px-3.5 lg:text-[14px] ${
                      active
                        ? "bg-[var(--cs-title-navy)] text-white"
                        : "text-[var(--cs-title-navy)] hover:bg-[#EEF4F8]"
                    }`}
                  >
                    {item.label}
                  </NavItem>
                );
              })}
            </nav>

            {showLogin ? (
              <div className="ml-auto hidden lg:flex">
                <Link
                  to="/login"
                  className="cs-cta-button cs-cta-button--compact inline-flex items-center justify-center gap-2 rounded-full bg-[var(--cs-title-navy)] px-4 py-1.5 text-[13px] font-semibold text-white hover:bg-[var(--cs-title-navy)]"
                >
                  <span>Inloggen</span>
                </Link>
              </div>
            ) : null}

            {showLogout ? (
              <div className="ml-auto hidden lg:flex">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center justify-center rounded-full border border-[#EEF4F8] bg-white px-4 py-1.5 text-[13px] font-semibold text-[var(--cs-navy)] shadow-[0_12px_28px_rgb(0_0_0_/_0.08)] transition-all duration-200 hover:-translate-y-[1px]"
                >
                  Uitloggen
                </button>
              </div>
            ) : null}

            <div className="ml-auto flex items-center gap-2 sm:hidden">
              {showLogin ? (
                <Link
                  to="/login"
                  className="cs-cta-button cs-cta-button--compact inline-flex items-center justify-center gap-2 rounded-full bg-[var(--cs-title-navy)] px-3.5 py-1.5 text-[13px] font-semibold text-white"
                >
                  <span>Inloggen</span>
                </Link>
              ) : null}

              {showLogout ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center justify-center rounded-full border border-[#EEF4F8] bg-white px-3.5 py-1.5 text-[13px] font-semibold text-[var(--cs-navy)] shadow-[0_12px_28px_rgb(0_0_0_/_0.08)]"
                >
                  Uitloggen
                </button>
              ) : null}

              <button
                type="button"
                aria-expanded={mobileMenuOpen}
                aria-controls="cs-marketing-mobile-nav"
                onClick={onToggleMenu}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#EEF4F8] bg-white text-[var(--cs-navy)] shadow-[0_12px_28px_rgb(0_0_0_/_0.08)] transition-colors hover:bg-[#EEF4F8]"
              >
                <MarketingIcon name="menu" className="h-4 w-4" />
              </button>
            </div>
          </div>

          {mobileMenuOpen ? (
            <div
              id="cs-marketing-mobile-nav"
              className="mt-3 rounded-[28px] border border-[#EEF4F8] bg-white p-3 shadow-[0_18px_48px_rgb(0_0_0_/_0.12)] sm:hidden"
            >
              <nav className="flex flex-col gap-1" aria-label="Mobile primary">
                {items.map((item) => {
                  const active = activeItem === item.href;

                  return (
                    <NavItem
                      key={`mobile-${item.label}`}
                      href={item.href}
                      onClick={onItemClick?.(item.href)}
                      className={`rounded-[20px] px-4 py-3 text-[15px] font-medium transition-colors ${
                          active
                            ? "bg-[var(--cs-title-navy)] text-white"
                            : "text-[var(--cs-title-navy)] hover:bg-[#EEF4F8]"
                      }`}
                    >
                      {item.label}
                    </NavItem>
                  );
                })}
              </nav>
            </div>
          ) : null}
        </header>
      </div>
    </div>
  );
}
